import { createContext, useContext, useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, obj = user
  const [favSlugs, setFavSlugs] = useState([]);

  const refreshFavourites = async () => {
    try {
      const { data } = await api.get("/me/favourite-slugs");
      setFavSlugs(data);
    } catch {
      setFavSlugs([]);
    }
  };

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) {
      return; // AuthCallback will establish the session first
    }
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
        refreshFavourites();
      } catch {
        setUser(false);
      }
    })();
  }, []);

  const login = async (identifier, password) => {
    const { data } = await api.post("/auth/login", { identifier, password });
    setUser(data);
    refreshFavourites();
    return data;
  };

  const register = async (username, email, password, account_type = "member") => {
    const { data } = await api.post("/auth/register", { username, email, password, account_type });
    setUser(data);
    return data;
  };

  const setUsername = async (username) => {
    const { data } = await api.post("/auth/username", { username });
    setUser(data);
    return data;
  };

  const refreshUser = async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch {
      setUser(false);
      return false;
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {}
    setUser(false);
    setFavSlugs([]);
  };

  const toggleFavourite = async (slug) => {
    try {
      const { data } = await api.post(`/companions/${slug}/favourite`);
      setFavSlugs((prev) =>
        data.favourited ? [...prev, slug] : prev.filter((s) => s !== slug)
      );
      return data.favourited;
    } catch (err) {
      if (err.response?.status === 403) {
        const e = new Error("premium");
        e.premium = true;
        throw e;
      }
      throw err;
    }
  };

  const isPremium = !!user && (user.role === "admin" || user.membership === "premium");

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) return; // handled by AuthCallback
  }, []);

  return (
    <AuthContext.Provider
      value={{ user, login, register, setUsername, refreshUser, logout, favSlugs, toggleFavourite, refreshFavourites, isPremium }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
export { formatApiErrorDetail };
